/**
 * Straight-multi rules for the slip (migration 0049 contract).
 *
 * A multi is 2–10 legs on DIFFERENT fixtures, priced by multiplying the
 * accepted per-leg odds; one stake, one fee. Same Game Multis are out —
 * the server rejects a repeated fixture with `multi_same_fixture`, so the
 * slip never lets one in: picking a second selection on a fixture that is
 * already on the slip REPLACES that leg (a punter changing their mind), it
 * does not add one. The server owns every number; these are previews.
 */
import type { Market, Selection } from '@/types/api';

export const MAX_LEGS = 10;

export interface Leg {
  market: Market;
  selection: Selection;
}

/** Market ids are `{fixtureId}:{marketType}`; fall back to the event identity. */
export function fixtureKey(market: Market): string {
  const i = market.id.indexOf(':');
  if (i > 0) return market.id.slice(0, i);
  return `${market.sport}|${market.eventLabel}|${market.startAt}`;
}

export type PickResult =
  | { legs: Leg[]; outcome: 'added' | 'replaced' | 'removed' }
  | { legs: Leg[]; outcome: 'full' };

/**
 * Toggle semantics: same selection again → removed; another selection on a
 * fixture already on the slip → replaced in place; otherwise appended, up to
 * MAX_LEGS.
 */
export function pickLeg(legs: Leg[], market: Market, selection: Selection): PickResult {
  const key = fixtureKey(market);
  const idx = legs.findIndex((l) => fixtureKey(l.market) === key);
  if (idx >= 0) {
    const existing = legs[idx];
    if (existing && existing.selection.id === selection.id && existing.market.id === market.id) {
      return { legs: legs.filter((_, i) => i !== idx), outcome: 'removed' };
    }
    const next = legs.slice();
    next[idx] = { market, selection };
    return { legs: next, outcome: 'replaced' };
  }
  if (legs.length >= MAX_LEGS) return { legs, outcome: 'full' };
  return { legs: [...legs, { market, selection }], outcome: 'added' };
}

export function removeLeg(legs: Leg[], selectionId: string): Leg[] {
  return legs.filter((l) => l.selection.id !== selectionId);
}

/** Product of the leg odds, as a number for preview. The server computes the exact decimal. */
export function combinedOdds(legs: Leg[]): number {
  return legs.reduce((acc, l) => acc * l.selection.decimalOdds, 1);
}

/** Stake-inclusive potential payout preview: floor(stake × product). */
export function previewPayoutCents(stakeCents: number, odds: number): number {
  return Math.floor(stakeCents * odds);
}

/** Any delayed leg makes the ticket delayed for the 0037 caps. */
export function anyDelayed(legs: Leg[]): boolean {
  return legs.some((l) => l.selection.dataDelayed);
}

export function formatOdds(odds: number): string {
  return odds >= 100 ? odds.toFixed(0) : odds.toFixed(2);
}
